import {
  ActionIcon,
  Box,
  Button,
  Chip,
  Container,
  Divider,
  Grid,
  Badge,
  Group,
  HoverCard,
  Indicator,
  Modal,
  MultiSelect,
  Paper,
  Popover,
  SegmentedControl,
  SimpleGrid,
  Skeleton,
  Stack,
  Table,
  Text,
  ThemeIcon,
  Title,
  UnstyledButton,
  useComputedColorScheme,
  useMantineTheme,
} from "@mantine/core";
import { connect, useDispatch } from "react-redux";
import "@mantine/charts/styles.css";
import { AreaChart, BarChart } from "@mantine/charts";
import {
  CartesianGrid,
  ReferenceArea,
  ReferenceLine,
  ResponsiveContainer,
  Scatter,
  ScatterChart,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import {
  IconChevronLeft,
  IconChevronRight,
  IconInfoCircle,
  IconPhoto,
  // IconTrendingDown,
  // IconTrendingUp,
} from "@tabler/icons-react";
import {
  useElementSize,
  useIntersection,
  useResizeObserver,
} from "@mantine/hooks";
import {
  cloneElement,
  memo,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  AnimatePresence,
  MotionConfig,
  animate,
  motion,
  useReducedMotion,
} from "motion/react";
import moment from "moment";
import DateFilterControl from "core/components/dateFilterPanel/DateFilterControl";
import { PrimaryBadge } from "core/components/badge/PrimaryBadge";
import FilterButton from "core/components/filterButton/FilterButton";
import FormInputWrapper from "core/components/formInputWrapper/FormInputWrapper";
import CustomDatePicker from "core/components/datePicker/CustomDatePicker";
import CaptureDetail from "core/components/capture/CaptureDetail";
import ImagePreview from "core/components/imagePreview/ImagePreview";
import { EmptyState } from "core/components/emptyState/EmptyState";
import Th from "core/components/table/Thead";
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table";
import {
  Link,
  Route,
  Routes,
  useLocation,
  useNavigate,
} from "react-router-dom";
import {
  DateRanges,
  DateSelections,
  filterSamples,
  formatNumber,
  getClientLabel,
  getDateRanges,
} from "utils/helper";
import { logger } from "utils/logger";
import {
  PriorityTypesMap,
  SAMPLE_STATUSES,
  URGENCY_LEVELS,
  filterByCreated,
  normalizeInventory,
} from "./inventoryStatus";
import {
  fetchLatestStyles,
  fetchStylesByUnit,
  getStylesSuccess,
  loadInventories,
  loadScans,
  setDashboardFilter,
  setDashboardPrefs,
  setInventoryFilter,
} from "./inventorySlice";
import { AppConstants, getCompanyTableColumns } from "utils/constants";
import {
  CATEGORY_COLOR,
  CHANNEL_COLOR,
  ERRANT_COLOR,
  OVERVIEW_SERIES,
  STATUS_COLORS,
  TREND_BAD,
  TREND_GOOD,
  URGENCY_COLORS,
  mantineColor,
} from "./inventoryColors";
import {
  EMPTY_DASHBOARD_FILTER,
  buildInventoryDashboard,
  countDashboardFilters,
  getDashboardFilterOptions,
} from "./inventoryAggregates";
import IconSelectBox from "./features/inventories/IconSelectBox";
import classes from "./InventoryModule.module.scss";
import Inventories from "./features/inventories/Inventories";

const seriesColor = (name) =>
  OVERVIEW_SERIES.find((item) => item.name === name)?.color;

const ALL_SERIES = OVERVIEW_SERIES.map((item) => item.name);

// Top stat tiles. Each is tied to an Inventory Overview series: the dot shares
// its colour and clicking the tile solos that series on the chart.
// `invertTrend` marks stats where going down is the good direction.
const STAT_TILES = [
  { key: "totalInventory", title: "Total Inventory", series: "Inventory" },
  { key: "assets", title: "Assets", series: "Assets" },
  { key: "checkedIn", title: "Samples Checked In", series: "Checked In" },
  { key: "scheduled", title: "Scheduled Styles", series: "Scheduled" },
  { key: "inRetouching", title: "Samples In Retouching", series: "Retouching" },
  { key: "distributed", title: "Distributed Samples", series: "Distributed" },
  {
    key: "errantScans",
    title: "Errant Scans",
    series: "Errant Scans",
    invertTrend: true,
  },
];

const STATUS_OPTIONS = SAMPLE_STATUSES.map((status) => ({
  name: status,
  color: STATUS_COLORS[status],
}));

const sumValues = (items = []) =>
  items.reduce((sum, item) => sum + item.value, 0);

// ---- Motion ----------------------------------------------------------------
// Shared easing/timings so every entrance on the dashboard feels the same.
const EASE_OUT = [0.22, 1, 0.36, 1];

// Page-level stagger: each <Reveal> registered under it fades/rises in turn.
const pageVariants = {
  hidden: {},
  show: { transition: { staggerChildren: 0.06, delayChildren: 0.05 } },
};

const revealVariants = {
  hidden: { opacity: 0, y: 12 },
  show: { opacity: 1, y: 0, transition: { duration: 0.45, ease: EASE_OUT } },
};

// Fades/rises a block in as part of the page stagger. `fill` keeps panels at
// full height inside grid columns.
const Reveal = ({ children, fill, style }) => (
  <motion.div
    variants={revealVariants}
    style={fill ? { height: "100%", ...style } : style}
  >
    {children}
  </motion.div>
);

// Counts up from the previous value to the new one.
const AnimatedNumber = ({ value }) => {
  const ref = useRef(null);
  const current = useRef(0);
  const reduceMotion = useReducedMotion();

  useEffect(() => {
    const node = ref.current;
    if (!node || value == null) return undefined;

    if (reduceMotion) {
      current.current = value;
      node.textContent = formatNumber(value);
      return undefined;
    }

    const controls = animate(current.current, value, {
      duration: 0.9,
      ease: EASE_OUT,
      onUpdate: (latest) => {
        current.current = latest;
        node.textContent = formatNumber(Math.round(latest));
      },
    });
    return () => controls.stop();
  }, [value, reduceMotion]);

  return <span ref={ref}>0</span>;
};

// Where the inventory panels' "Show All" goes (the Inventories list).
const INVENTORY_LIST_ROUTE = "/inventory/list";

// Number of most recent distributed styles shown, with their assets loaded.
const LATEST_DISTRIBUTIONS_COUNT = 10;

// The stored date filter for weekStart..weekEnd ("YYYY-MM-DD"; weekEnd
// defaults to a week from the start), so DateFilterControl's label and range
// match what the dashboard shows. The range is capped at today, and the label
// picks the preset whose start and (capped) end both match.
const weekDataFilter = (weekStart, weekEnd, dataFilter) => {
  const today = getDateRanges({ value: DateRanges.TODAY });
  const todayEnd = moment(today.endDate);
  const start = moment.min(
    moment(weekStart).startOf("day"),
    moment(today.startDate),
  );
  const end = moment.max(
    moment.min(
      weekEnd
        ? moment(weekEnd).endOf("day")
        : start.clone().add(6, "days").endOf("day"),
      todayEnd,
    ),
    start.clone().endOf("day"),
  );

  const preset = [
    DateRanges.TODAY,
    DateRanges.THIS_WEEK,
    DateRanges.LAST_WEEK,
  ].find((value) => {
    const range = getDateRanges({ value });
    return (
      start.isSame(range.startDate, "day") &&
      end.isSame(moment.min(moment(range.endDate), todayEnd), "day")
    );
  });
  const item =
    DateSelections.find(
      (selection) => selection.value === (preset ?? DateRanges.CUSTOM),
    ) ?? DateSelections[1];

  return {
    ...dataFilter,
    dateRange: { item, value: [start.toDate(), end.toDate()] },
  };
};

const InventoryDashboard = ({
  user,
  inventoryFilter,
  dashboardFilter,
  dashboardPrefs,
  styles,
  inventories,
  inventoriesError,
  scans,
  scansError,
  success,
  error,
}) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();

  // Selected range: the DateFilterControl writes ?weekstart= and ?weekend= to
  // the url (mirrors HomeModule), falling back to the stored date filter and
  // then the current week.
  const weekStart = useMemo(() => {
    const fromUrl = new URLSearchParams(location.search).get("weekstart");
    if (fromUrl) return fromUrl;
    const filterDate =
      inventoryFilter?.dataFilter?.dateRange?.value?.[0] ?? null;
    return moment(
      filterDate ?? getDateRanges({ value: DateRanges.THIS_WEEK }).startDate,
    ).format("YYYY-MM-DD");
  }, [location.search, inventoryFilter?.dataFilter]);

  // Last day of the range; null means a week from weekStart.
  const weekEnd = useMemo(() => {
    const fromUrl = new URLSearchParams(location.search).get("weekend");
    if (fromUrl) return fromUrl;
    if (new URLSearchParams(location.search).get("weekstart")) return null;
    const filterDate =
      inventoryFilter?.dataFilter?.dateRange?.value?.[1] ?? null;
    return filterDate ? moment(filterDate).format("YYYY-MM-DD") : null;
  }, [location.search, inventoryFilter?.dataFilter]);

  // Keep the stored date filter on the selected range, whether it came from
  // applying the date filter or from a ?weekstart=&weekend= link.
  const applyWeek = (week, end) => {
    const dataFilter = weekDataFilter(week, end, inventoryFilter?.dataFilter);
    const current = inventoryFilter?.dataFilter?.dateRange;
    const unchanged =
      current?.item?.value === dataFilter.dateRange.item.value &&
      moment(current?.value?.[0]).isSame(
        dataFilter.dateRange.value[0],
        "day",
      ) &&
      moment(current?.value?.[1]).isSame(dataFilter.dateRange.value[1], "day");
    if (!unchanged) {
      dispatch(setInventoryFilter({ ...inventoryFilter, dataFilter }));
    }
  };

  const urlParams = new URLSearchParams(location.search);
  const urlWeekStart = urlParams.get("weekstart");
  const urlWeekEnd = urlParams.get("weekend");
  useEffect(() => {
    if (urlWeekStart) applyWeek(urlWeekStart, urlWeekEnd);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [urlWeekStart, urlWeekEnd]);

  // Aggregate once every inventory and errant scan page is in. A failed fetch
  // counts as empty rather than falling back to mock numbers.
  const inventoryReady =
    Boolean(inventories?.complete) || Boolean(inventoriesError);
  const scansReady = Boolean(scans?.complete) || Boolean(scansError);
  const scanRecords = scans?.data;

  // Apply the Inventories list filter (inventoryFilter) as soon as the records are
  // in, with the same normalising and filterSamples the list uses, so the
  // dashboard covers the same records the list shows.
  const columns = useMemo(
    () =>
      user?.clientCode
        ? getCompanyTableColumns(user.clientCode, "inventories")
        : [],
    [user?.clientCode],
  );
  const allRecords = inventories?.data;
  const listRecords = useMemo(
    () =>
      allRecords ??
      [][
        // filterSamples((allRecords ?? []).map(normalizeInventory), inventoryFilter ?? {}, columns) ?? [],
        (allRecords, inventoryFilter, columns)
      ],
  );

  // Then only records created within the selected date range.
  const inventoryRecords = useMemo(
    () =>
      filterByCreated(listRecords, [
        weekStart,
        weekEnd ?? moment(weekStart).add(6, "days").format("YYYY-MM-DD"),
      ]),
    [listRecords, weekStart, weekEnd],
  );

  logger.log(inventoryFilter);
  // Errant scans have their own dates, so only the list filter (not the
  // created range) limits them to matching records.
  const recordsFiltered = listRecords.length !== (allRecords ?? []).length;

  const dashboard = useMemo(() => {
    if (!inventoryReady || !scansReady) return null;
    return buildInventoryDashboard(inventoriesError ? [] : inventoryRecords, {
      weekStart,
      weekEnd,
      scans: scansError ? [] : scanRecords,
      filter: dashboardFilter,
      recordsFiltered,
    });
  }, [
    dashboardFilter,
    recordsFiltered,
    inventoryReady,
    scansReady,
    inventoriesError,
    scansError,
    inventoryRecords,
    scanRecords,
    weekStart,
    weekEnd,
  ]);

  const isLoading = !dashboard;
  const filterCount = countDashboardFilters(dashboardFilter);
  const filterOptions = useMemo(
    () => getDashboardFilterOptions(inventoryRecords ?? []),
    [inventoryRecords],
  );
  const [filterOpen, setFilterOpen] = useState(false);
  const [stylesLoading, setStylesLoading] = useState(true);
  const [selectedAsset, setSelectedAsset] = useState(null);
  // Saved series selection (store, persisted); null means all. Names no
  // longer in the chart are dropped.
  const visibleSeries =
    dashboardPrefs?.visibleSeries?.filter((name) =>
      ALL_SERIES.includes(name),
    ) ?? ALL_SERIES;
  const setVisibleSeries = (value) =>
    dispatch(setDashboardPrefs({ visibleSeries: value }));

  // Sentinel just above the sticky header; once it scrolls out of view the
  // header is "stuck" and gets a divider/shadow.
  const { ref: stickySentinelRef, entry: stickySentinel } = useIntersection({
    threshold: 0,
  });
  const headerStuck = stickySentinel ? !stickySentinel.isIntersecting : false;

  const stats = dashboard?.stats;
  const statChanges = dashboard?.statChanges;

  // Clicking a tile solos its series; clicking the soloed tile restores all.
  const toggleSoloSeries = (name) => {
    setVisibleSeries(
      visibleSeries.length === 1 && visibleSeries[0] === name
        ? ALL_SERIES
        : [name],
    );
  };
  // Distributed styles that match the filter (matched to inventory by style
  // number); all of them when no filter is on.
  const sampleKeys = dashboard?.sampleKeys;
  const filteredStyles = useMemo(
    () =>
      sampleKeys
        ? (styles ?? []).filter((style) =>
            sampleKeys.has(String(style.styleNumber)),
          )
        : (styles ?? []),
    [styles, sampleKeys],
  );
  const latestStyles = sortByDistributionDate(filteredStyles).slice(
    0,
    LATEST_DISTRIBUTIONS_COUNT,
  );

  // Fetch distributed styles for the selected week.
  useEffect(() => {
    setStylesLoading(true);
    dispatch(fetchStylesByUnit({ status: "", weekStart }));
  }, [weekStart, dispatch]);

  // Once the style list arrives, load full details (incl. assets) for the
  // latest few so their thumbnails can be shown.
  // Ids already requested, so changing the filter only loads the styles that
  // newly make the top few. Reset when a new week's list arrives.
  const requestedStyleIds = useRef(new Set());
  useEffect(() => {
    if (success?.type === getStylesSuccess.toString()) {
      setStylesLoading(false);
      requestedStyleIds.current = new Set();
    }
  }, [success]);

  useEffect(() => {
    if (stylesLoading) return;
    const ids = latestStyles
      .map((val) => val.styleNumber)
      .filter((id) => !requestedStyleIds.current.has(id));
    if (ids.length) {
      ids.forEach((id) => requestedStyleIds.current.add(id));
      dispatch(fetchLatestStyles(ids));
    }
  }, [latestStyles, stylesLoading, dispatch]);

  // Inventory and errant scans aren't week-scoped, so they're loaded once
  // (all pages) and aggregated for the selected week above.
  // Reuses what's already loaded while it's fresh (e.g. coming back from the
  // list) rather than replacing it with a full reload.
  useEffect(() => {
    dispatch(loadInventories(AppConstants.CACHE_DELAY_LONG));
    dispatch(loadScans(AppConstants.CACHE_DELAY_LONG));
  }, [dispatch]);

  useEffect(() => {
    if (inventoriesError) {
      logger.log("Inventory fetch error", inventoriesError);
    }
  }, [inventoriesError]);

  useEffect(() => {
    if (error) {
      logger.log("Inventory styles error", error);
      setStylesLoading(false);
    }
  }, [error]);

  return (
    // reducedMotion="user" turns transform/layout animation off when the OS
    // asks for reduced motion.
    <MotionConfig reducedMotion="user">
      <Container fluid px={"lg"} py={"lg"} style={{ marginLeft: 0 }}>
        <div ref={stickySentinelRef} style={{ height: 1, marginTop: -1 }} />
        <Group
          mb={"md"}
          justify="space-between"
          wrap="wrap"
          gap={"md"}
          className={`${classes.stickyHeader} ${headerStuck ? classes.stuck : ""}`}
        >
          <Title order={2}>Welcome back, {user?.first} 👋</Title>
          <Group>
            <DateFilterControl
              filter={inventoryFilter}
              onApply={applyWeek}
              weekOnly={false}
            />
            <Popover
              shadow="sm"
              opened={filterOpen}
              onChange={setFilterOpen}
              width={500}
              withArrow
              closeOnClickOutside
              position="bottom-end"
            >
              <Popover.Target>
                <div>
                  <FilterButton
                    count={filterCount}
                    onClick={() => setFilterOpen((o) => !o)}
                    onClear={(e) => {
                      e.stopPropagation();
                      dispatch(setDashboardFilter(EMPTY_DASHBOARD_FILTER));
                    }}
                  />
                </div>
              </Popover.Target>
              <Popover.Dropdown>
                {filterOpen ? (
                  <InventoryFilterPanel
                    filter={dashboardFilter}
                    options={filterOptions}
                    onCancel={() => setFilterOpen(false)}
                    onApply={(values) => {
                      dispatch(setDashboardFilter(values));
                      setFilterOpen(false);
                    }}
                  />
                ) : null}
              </Popover.Dropdown>
            </Popover>
          </Group>
        </Group>

        <motion.div variants={pageVariants} initial="hidden" animate="show">
          <SimpleGrid cols={{ base: 1, xs: 2, md: 4, xl: 7 }} mb={"md"}>
            {STAT_TILES.map((tile) => (
              <Reveal key={tile.key} fill>
                <StatCard
                  isLoading={isLoading}
                  title={tile.title}
                  value={stats?.[tile.key]}
                  change={statChanges?.[tile.key]}
                  invertTrend={tile.invertTrend}
                  color={seriesColor(tile.series)}
                  active={
                    visibleSeries.length === 1 &&
                    visibleSeries[0] === tile.series
                  }
                  onClick={() => toggleSoloSeries(tile.series)}
                />
              </Reveal>
            ))}
          </SimpleGrid>

          {/* Trend + current stage breakdown share a row on wide screens. */}
          <Grid mb={"md"}>
            <Grid.Col span={{ base: 12, xl: 8 }}>
              <Reveal fill>
                <InventoryOverviewPanel
                  isLoading={isLoading}
                  data={dashboard?.inventoryOverview}
                  visible={visibleSeries}
                  onVisibleChange={setVisibleSeries}
                />
              </Reveal>
            </Grid.Col>
            <Grid.Col span={{ base: 12, xl: 4 }}>
              <Reveal fill>
                <DashboardPanel
                  title="Items per Stage"
                  fill
                  count={sumValues(dashboard?.itemsPerStage)}
                  onViewAll={() => navigate(INVENTORY_LIST_ROUTE)}
                >
                  <StageBarChart
                    isLoading={isLoading}
                    items={dashboard?.itemsPerStage}
                  />
                </DashboardPanel>
              </Reveal>
            </Grid.Col>
          </Grid>

          <Reveal>
            <SampleUrgencyPanel
              isLoading={isLoading}
              samples={dashboard?.urgencySamples}
              otbLabel={getClientLabel(user?.clientCode, "otbDate", "OTB Date")}
              prefs={dashboardPrefs}
              onPrefsChange={(prefs) => dispatch(setDashboardPrefs(prefs))}
            />
          </Reveal>

          {/* Three across on large screens; two + one full-width below that. */}
          <Grid mb={"md"}>
            <Grid.Col span={{ base: 12, sm: 6, lg: 4 }}>
              <Reveal fill>
                <DashboardPanel
                  title="Errant Scan Reasons"
                  count={sumValues(dashboard?.errantReasons)}
                  onViewAll={() => navigate(INVENTORY_LIST_ROUTE)}
                >
                  <VolumeList
                    isLoading={isLoading}
                    items={dashboard?.errantReasons}
                    barColor={ERRANT_COLOR}
                  />
                </DashboardPanel>
              </Reveal>
            </Grid.Col>

            <Grid.Col span={{ base: 12, sm: 6, lg: 4 }}>
              <Reveal fill>
                <CategoriesPanel
                  isLoading={isLoading}
                  categories={dashboard?.categories}
                />
              </Reveal>
            </Grid.Col>

            <Grid.Col span={{ base: 12, lg: 4 }}>
              <Reveal fill>
                <ChannelsPanel
                  isLoading={isLoading}
                  channels={dashboard?.channels}
                />
              </Reveal>
            </Grid.Col>
          </Grid>

          <Reveal>
            <DashboardPanel
              title="Latest Distributions"
              count={latestStyles.length}
              onViewAll={() => navigate("/distributions")}
            >
              <LatestDistributionsSlider
                isLoading={stylesLoading}
                items={latestStyles}
                user={user}
                onAssetClick={setSelectedAsset}
              />
            </DashboardPanel>
          </Reveal>
        </motion.div>

        <Modal
          opened={!!selectedAsset}
          size={"lg"}
          title={selectedAsset?.name}
          trapFocus={true}
          fullScreen
          onClose={() => setSelectedAsset(null)}
        >
          {selectedAsset ? (
            <CaptureDetail
              showSettings={false}
              resoloveImage={true}
              image={selectedAsset}
              src={selectedAsset.preview}
              onImageEvent={() => {}}
              onCaptureChange={() => {}}
            />
          ) : null}
        </Modal>
      </Container>
    </MotionConfig>
  );
};

const mapStateToProps = (state) => {
  return {
    user: state.auth.user,
    inventoryFilter: state.inventoryReducer.inventoryFilter,
    dashboardFilter: state.inventoryReducer.dashboardFilter,
    dashboardPrefs: state.inventoryReducer.dashboardPrefs,
    styles: state.inventoryReducer.styles,
    inventories: state.inventoryReducer.inventories,
    inventoriesError: state.inventoryReducer.inventoriesError,
    scans: state.inventoryReducer.scans,
    scansError: state.inventoryReducer.scansError,
    success: state.inventoryReducer.success,
    error: state.inventoryReducer.error,
  };
};

const ConnectedInventoryDashboard =
  connect(mapStateToProps)(InventoryDashboard);

// The list layout fills the module height (like WipModule), so it gets the
// module-container wrapper; the dashboard scrolls with the page.
const InventoryModule = () => (
  <Routes>
    <Route index element={<ConnectedInventoryDashboard />} />
    <Route
      path="/list"
      element={
        <Box id="inventoryModule" className="module-container">
          <Inventories />
        </Box>
      }
    />
  </Routes>
);

export default InventoryModule;

// `fill` makes the panel a flex column so a child can stretch to the
// remaining height (e.g. to match a taller neighbour in the same grid row).
const DashboardPanel = ({
  title,
  count,
  onViewAll,
  actions,
  fill,
  children,
}) => {
  return (
    <Paper
      p={"lg"}
      withBorder
      h={"100%"}
      style={fill ? { display: "flex", flexDirection: "column" } : undefined}
    >
      <Group justify="space-between" mb={"lg"} wrap="wrap" gap={"sm"}>
        <Group gap={"sm"}>
          <Title order={4}>{title}</Title>
          {count !== undefined ? (
            <PrimaryBadge>{formatNumber(count)}</PrimaryBadge>
          ) : null}
        </Group>
        {actions}
        {onViewAll ? (
          <Button variant="default" onClick={onViewAll}>
            Show All
          </Button>
        ) : null}
      </Group>
      {children}
    </Paper>
  );
};

// Urgency options with the same icons/colours as the list's priority filter.
// Urgency names match the PriorityTypesMap labels.
const URGENCY_OPTIONS = Object.values(PriorityTypesMap).map((type) => ({
  ...type,
  value: type.label,
}));

const toDate = (value) => (value ? moment(value, "YYYY-MM-DD").toDate() : null);
const toDay = (date) => (date ? moment(date).format("YYYY-MM-DD") : "");

// Edits a draft of the dashboard filter; nothing changes until Apply.
const InventoryFilterPanel = ({ filter, options, onCancel, onApply }) => {
  const [draft, setDraft] = useState({ ...EMPTY_DASHBOARD_FILTER, ...filter });
  const set = (key) => (value) =>
    setDraft((current) => ({ ...current, [key]: value }));

  return (
    <Box>
      <FormInputWrapper label="Department" labelWidth={"110px"}>
        <MultiSelect
          placeholder="All departments"
          searchable
          clearable
          comboboxProps={{ withinPortal: false }}
          data={options.departments}
          value={draft.departments}
          onChange={set("departments")}
        />
      </FormInputWrapper>

      <FormInputWrapper label="Channel" labelWidth={"110px"}>
        <MultiSelect
          placeholder="All channels"
          searchable
          clearable
          comboboxProps={{ withinPortal: false }}
          data={options.channels}
          value={draft.channels}
          onChange={set("channels")}
        />
      </FormInputWrapper>

      <FormInputWrapper label="Status" labelWidth={"110px"}>
        <MultiSelect
          placeholder="All statuses"
          searchable
          clearable
          comboboxProps={{ withinPortal: false }}
          data={SAMPLE_STATUSES}
          value={draft.statuses}
          onChange={set("statuses")}
        />
      </FormInputWrapper>

      <FormInputWrapper label="Urgency" labelWidth={"110px"}>
        <IconSelectBox
          data={URGENCY_OPTIONS}
          placeholder="All urgencies"
          value={draft.urgencies}
          onChange={set("urgencies")}
        />
      </FormInputWrapper>

      <FormInputWrapper label="OTB Date" labelWidth={"110px"}>
        <Group wrap={"nowrap"}>
          <CustomDatePicker
            style={{ width: 150 }}
            placeholder="From OTB Date"
            clearable
            value={toDate(draft.otbStart)}
            maxDate={toDate(draft.otbEnd) ?? undefined}
            onChange={(date) => set("otbStart")(toDay(date))}
          />
          <Text>to</Text>
          <CustomDatePicker
            style={{ width: 150 }}
            placeholder="To OTB Date"
            clearable
            value={toDate(draft.otbEnd)}
            minDate={toDate(draft.otbStart) ?? undefined}
            onChange={(date) => set("otbEnd")(toDay(date))}
          />
        </Group>
      </FormInputWrapper>

      <Divider />
      <Group justify="space-between" mt="lg">
        <Group gap={"xs"}>
          <Button variant="subtle" size="sm" onClick={onCancel}>
            Cancel
          </Button>
          <Button
            variant="subtle"
            size="sm"
            color="gray"
            onClick={() => setDraft({ ...EMPTY_DASHBOARD_FILTER })}
          >
            Clear
          </Button>
        </Group>
        <Button size="sm" variant="filled" onClick={() => onApply(draft)}>
          Apply
        </Button>
      </Group>
    </Box>
  );
};

// `color` ties the tile to its Inventory Overview series, shown as the same
// indicator dot used on the HomeModule stat tiles. `change` is the % change vs
// last week; `invertTrend` flips which direction is coloured as good.
const StatCard = ({
  isLoading,
  title,
  value,
  color,
  change,
  invertTrend = false,
  active = false,
  onClick,
}) => {
  // Trend ("+5% vs last week") hidden for now - uncomment here and below.
  // const isUp = change >= 0;
  // const isGood = invertTrend ? !isUp : isUp;
  // const TrendIcon = isUp ? IconTrendingUp : IconTrendingDown;
  // const trendColor = isGood ? TREND_GOOD : TREND_BAD;

  return (
    <Paper
      p={"lg"}
      withBorder
      role="button"
      tabIndex={0}
      aria-pressed={active}
      className={`${classes.statCard} ${active ? classes.statCardActive : ""}`}
      style={active && color ? { borderColor: mantineColor(color) } : undefined}
      onClick={onClick}
      onKeyDown={(event) => {
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          onClick?.();
        }
      }}
    >
      <Stack gap={"xs"} align="flex-start">
        <Indicator
          zIndex={2}
          position="middle-start"
          color={color}
          disabled={!color}
        >
          <Text
            style={{ paddingLeft: 12 }}
            c="dimmed"
            tt="uppercase"
            fw={700}
            size="xs"
            className={classes.label}
          >
            {title}
          </Text>
        </Indicator>
        {isLoading ? (
          <Skeleton h={32} w={80} />
        ) : (
          <Text fw={700} size={"1.75rem"} lh={1.2}>
            <AnimatedNumber value={value} />
          </Text>
        )}
        {/* {!isLoading && change !== undefined ? (
          <Group gap={4} wrap="nowrap">
            <TrendIcon size={14} stroke={2} color={mantineColor(`${trendColor}.6`)} />
            <Text size="xs" fw={600} c={`${trendColor}.6`}>
              {isUp ? "+" : ""}
              {change}%
            </Text>
            <Text size="xs" c="dimmed">
              vs last week
            </Text>
          </Group>
        ) : null} */}
      </Stack>
    </Paper>
  );
};

// Chip row for toggling chart series on/off, with an "All" chip that selects
// or clears every option. Chips double as the chart legend. `onHover` (optional)
// reports the option under the pointer, or null when it leaves.
const SeriesToggle = ({ allLabel, options, value, onChange, onHover }) => {
  const allSelected = value.length === options.length;
  return (
    <Group gap={"xs"} mb={"lg"}>
      <Chip
        size="sm"
        variant="outline"
        checked={allSelected}
        onChange={() =>
          onChange(allSelected ? [] : options.map((option) => option.name))
        }
      >
        {allLabel}
      </Chip>
      <Chip.Group multiple value={value} onChange={onChange}>
        {options.map((option) => (
          <Chip
            key={option.name}
            size="sm"
            variant="outline"
            value={option.name}
            color={option.color.split(".")[0]}
            wrapperProps={
              onHover
                ? {
                    onMouseEnter: () => onHover(option.name),
                    onMouseLeave: () => onHover(null),
                  }
                : undefined
            }
          >
            {option.name}
          </Chip>
        ))}
      </Chip.Group>
    </Group>
  );
};

// Totals (total inventory, and assets - photographs across the inventory) are
// far larger than the per-day series (checked in, retouching, distributed,
// errant scans), so when shown alongside them they move to the right-hand
// axis. Each gets its axis label name here.
const TOTAL_SERIES = ["Inventory", "Assets"];
const TOTAL_AXIS_NAMES = { Inventory: "Total inventory", Assets: "Assets" };

// Y axis label drawn against the axis's own box (the plot area's height), so
// it's centred on the chart and sits just outside the tick values. Mantine's
// yAxisLabel/rightYAxisLabel use Recharts' insideLeft/insideRight with a
// negative offset, which pushes them out to the panel edge.
const AXIS_LABEL_INSET = 6;
const axisLabel = (text, side) => ({
  content: ({ viewBox }) => {
    if (!viewBox) return null;
    const { x, y, width, height } = viewBox;
    const cx =
      side === "right" ? x + width - AXIS_LABEL_INSET : x + AXIS_LABEL_INSET;
    const cy = y + height / 2;
    return (
      <text
        x={cx}
        y={cy}
        transform={`rotate(${side === "right" ? 90 : -90}, ${cx}, ${cy})`}
        textAnchor="middle"
        dominantBaseline="central"
        fontSize={12}
        fill="var(--mantine-color-dimmed)"
      >
        {text}
      </text>
    );
  },
});
// Wider than Recharts' 60px default so 6 digit ticks (e.g. 180000) leave a
// clear gap before the label.
const LABELLED_AXIS_WIDTH = 88;
const SAMPLES_AXIS_PROPS = {
  width: LABELLED_AXIS_WIDTH,
  label: axisLabel("Samples", "left"),
};
// Right axis label names whichever totals are shown, e.g. "Total inventory / Assets".
const totalAxisProps = (names) => ({
  width: LABELLED_AXIS_WIDTH,
  label: axisLabel(
    names.map((name) => TOTAL_AXIS_NAMES[name] ?? name).join(" / "),
    "right",
  ),
});

const InventoryOverviewPanel = ({
  isLoading,
  data,
  visible,
  onVisibleChange,
}) => {
  const shown = OVERVIEW_SERIES.filter((item) => visible.includes(item.name));
  const isTotal = (item) => TOTAL_SERIES.includes(item.name);
  const splitAxis = shown.some(isTotal) && shown.some((item) => !isTotal(item));

  const series = shown.map((item) =>
    splitAxis && isTotal(item) ? { ...item, yAxisId: "right" } : item,
  );

  return (
    <DashboardPanel title="Inventory Overview">
      <SeriesToggle
        allLabel="All Series"
        options={OVERVIEW_SERIES}
        value={visible}
        onChange={onVisibleChange}
      />

      {isLoading ? (
        <Skeleton h={300} />
      ) : series.length ? (
        <AreaChart
          className={classes.chartAnimateIn}
          h={300}
          data={data}
          dataKey="date"
          series={series}
          curveType="linear"
          withDots={false}
          fillOpacity={0.15}
          // Axis ticks and tooltip values.
          valueFormatter={formatNumber}
          xAxisLabel="Date"
          withRightYAxis={splitAxis}
          yAxisProps={splitAxis ? SAMPLES_AXIS_PROPS : undefined}
          rightYAxisProps={
            splitAxis
              ? totalAxisProps(shown.filter(isTotal).map((item) => item.name))
              : undefined
          }
        />
      ) : (
        <Text c="dimmed" ta="center" py={"xl"}>
          Select a series to view the chart
        </Text>
      )}
    </DashboardPanel>
  );
};

// Deterministic 0..1 offset from a sample id so points in the same urgency
// column spread out without moving between renders.
const jitterFor = (id) => {
  let hash = 0;
  for (let i = 0; i < id.length; i++) {
    hash = (hash * 31 + id.charCodeAt(i)) % 1000;
  }
  return hash / 1000;
};

const urgencyIndex = (urgency) =>
  URGENCY_LEVELS.findIndex((level) => level.name === urgency);

// Same icon as the list's priority column. Urgency names match the
// PriorityTypesMap labels.
const UrgencyIcon = ({ urgency, size = "sm", iconSize = 14 }) => {
  const type = Object.values(PriorityTypesMap).find(
    (item) => item.label === urgency,
  );
  if (!type) return null;
  return (
    <ThemeIcon
      size={size}
      variant="light"
      color={URGENCY_COLORS[urgency]}
      style={{ flexShrink: 0 }}
    >
      {cloneElement(type.icon, { size: iconSize })}
    </ThemeIcon>
  );
};

// Samples not scanned within this many days are flagged as stale.
const STALE_SCAN_DAYS = 30;

const SCATTER_MARGIN = { top: 8, right: 16, bottom: 8, left: 0 };
const SCATTER_Y_AXIS_WIDTH = 56;

// Most dots drawn at once. Past this, each status keeps an even spread of its
// dots (by position) and the header notes how many are shown.
const MAX_SCATTER_POINTS = 1500;
// Past this many dots the pop-in animation is skipped; animating thousands of
// SVG nodes at once is what makes a busy chart stutter.
const SCATTER_ANIMATE_LIMIT = 400;
// Dots closer than this share one position: x in 1/40ths of a column, y in
// whole days. A dot is 12px across, so these would overlap anyway.
const SCATTER_X_BINS = 40;

// One dot (a single circle; hover styling is CSS). Memoised on its position
// and data, so tooltip/hover re-renders don't redraw every dot. Dimming is
// done per series (see the Scatter className), not per dot.
const ScatterPoint = memo(
  ({ cx, cy, payload, animate }) => {
    if (cx == null || cy == null) return null;
    return (
      <circle
        className={`${classes.scatterPoint} ${animate ? classes.scatterPopIn : ""}`}
        style={animate ? { animationDelay: `${payload.delay}ms` } : undefined}
        cx={cx}
        cy={cy}
        r={6}
        fill={mantineColor(STATUS_COLORS[payload.status])}
        stroke="var(--mantine-color-body)"
        strokeWidth={1.5}
      />
    );
  },
  (prev, next) =>
    prev.cx === next.cx &&
    prev.cy === next.cy &&
    prev.payload === next.payload &&
    prev.animate === next.animate,
);

// Every `step`th item, keeping the first and last, so a thinned series still
// covers the same spread.
const thin = (items, keep) => {
  if (items.length <= keep) return items;
  const step = items.length / keep;
  return Array.from(
    { length: keep },
    (_, index) => items[Math.floor(index * step)],
  );
};

const UrgencyRangeTick = ({ x, y, payload }) => {
  const level = URGENCY_LEVELS[Math.floor(payload.value)];
  if (!level) return null;
  return (
    <text
      x={x}
      y={y}
      dy={14}
      textAnchor="middle"
      fontSize={12}
      fill="var(--mantine-color-dimmed)"
    >
      {level.range}
    </text>
  );
};

const UrgencyTooltip = ({ active, payload }) => {
  if (!active || !payload?.length) return null;
  const { sample, count = 1 } = payload[0].payload;

  return (
    <Paper shadow="md" p={"sm"} withBorder style={{ minWidth: 180 }}>
      <Box>
        <Text size="sm" fw={700} mb={0}>
          {sample.name}
        </Text>
      </Box>
      <Text size="sm" c={"dimmed"} mb={6}>
        {sample.id}
      </Text>
      <Stack gap={4}>
        <Group gap={6} wrap="nowrap">
          <Box
            w={8}
            h={8}
            style={{
              borderRadius: "50%",
              backgroundColor: mantineColor(STATUS_COLORS[sample.status]),
            }}
          />
          <Text size="xs">{sample.status}</Text>
        </Group>
        <Group justify="space-between" gap={"md"}>
          <Text size="xs" c="dimmed">
            Urgency
          </Text>
          <Group gap={6} wrap="nowrap">
            <UrgencyIcon urgency={sample.urgency} size={18} iconSize={12} />
            <Text size="xs" fw={600}>
              {sample.urgency}
            </Text>
          </Group>
        </Group>
        <Group justify="space-between" gap={"md"}>
          <Text size="xs" c="dimmed">
            Last scanned
          </Text>
          <Text size="xs" fw={600}>
            {formatNumber(sample.daysSinceScanned)} days ago
          </Text>
        </Group>
        {count > 1 ? (
          <Text size="xs" c="dimmed">
            +{formatNumber(count - 1)} more sample{count === 2 ? "" : "s"} at
            this point
          </Text>
        ) : null}
      </Stack>
    </Paper>
  );
};

// Narrowest the urgency scatter/matrix get before scrolling sideways, so the
// eleven P0-P8 / Low / Error columns stay readable.
const URGENCY_MIN_WIDTH = 1040;

const UrgencyScatter = ({ samples, highlightStatus }) => {
  const columnCount = URGENCY_LEVELS.length;

  // One series per status so toggling a status mounts/unmounts only its
  // points, and the pop-in animation only plays for points that appear.
  //
  // Samples that would land on the same spot are merged into one dot (with a
  // count for the tooltip), then each status is thinned to its share of
  // MAX_SCATTER_POINTS.
  const { series, shownCount } = useMemo(() => {
    const merged = SAMPLE_STATUSES.map((status) => {
      const bins = new Map();
      samples.forEach((sample) => {
        if (sample.status !== status) return;
        const column = urgencyIndex(sample.urgency);
        const bin = Math.floor(jitterFor(String(sample.id)) * SCATTER_X_BINS);
        const key = `${column}|${bin}|${sample.daysSinceScanned}`;
        const existing = bins.get(key);
        if (existing) {
          existing.count += 1;
          return;
        }
        const x = column + 0.15 + ((bin + 0.5) / SCATTER_X_BINS) * 0.7;
        bins.set(key, {
          x,
          y: sample.daysSinceScanned,
          status,
          sample,
          count: 1,
          // Sweep in left to right, with a little scatter inside a column.
          delay: Math.round(
            (x / columnCount) * 600 + jitterFor(sample.id + status) * 120,
          ),
        });
      });
      // Sorted by position so thinning keeps an even spread.
      const points = [...bins.values()].sort((a, b) => a.x - b.x || a.y - b.y);
      return { status, points };
    }).filter((item) => item.points.length);

    const total = merged.reduce((sum, item) => sum + item.points.length, 0);
    const capped =
      total > MAX_SCATTER_POINTS
        ? merged.map((item) => ({
            ...item,
            points: thin(
              item.points,
              Math.max(
                1,
                Math.round((item.points.length / total) * MAX_SCATTER_POINTS),
              ),
            ),
          }))
        : merged;

    return {
      series: capped,
      shownCount: capped.reduce(
        (sum, item) =>
          sum + item.points.reduce((count, point) => count + point.count, 0),
        0,
      ),
    };
  }, [samples, columnCount]);

  const pointCount = series.reduce((sum, item) => sum + item.points.length, 0);
  const animate = pointCount <= SCATTER_ANIMATE_LIMIT;
  // Stable shape per animate setting, so Recharts gets the same renderer
  // between renders.
  const renderPoint = useMemo(
    () => (props) => <ScatterPoint {...props} animate={animate} />,
    [animate],
  );

  const columnCounts = URGENCY_LEVELS.map(
    (level) => samples.filter((sample) => sample.urgency === level.name).length,
  );

  const maxDays = Math.max(
    0,
    ...samples.map((sample) => sample.daysSinceScanned),
  );
  const yMax = Math.max(60, Math.ceil(maxDays / 15) * 15);
  const yTicks = Array.from(
    { length: yMax / 15 + 1 },
    (_, index) => index * 15,
  );

  if (!samples.length) {
    return (
      // <Text c="dimmed" ta="center" py={"xl"}>
      //   Select a status to view samples
      // </Text>
      <EmptyState>Select a status to view samples</EmptyState>
    );
  }

  return (
    // Eleven urgency columns need room; narrower screens scroll sideways.
    <Box className={classes.chartAnimateIn} style={{ overflowX: "auto" }}>
      <Box miw={URGENCY_MIN_WIDTH}>
        <Box
          className={classes.urgencyHeader}
          style={{
            paddingLeft: SCATTER_Y_AXIS_WIDTH + SCATTER_MARGIN.left,
            paddingRight: SCATTER_MARGIN.right,
          }}
        >
          {URGENCY_LEVELS.map((level, index) => {
            const color = mantineColor(URGENCY_COLORS[level.name]);
            return (
              <Box
                key={level.name}
                className={classes.urgencyHeaderCell}
                style={{ borderTopColor: color }}
              >
                <Group gap={6} wrap="nowrap">
                  <UrgencyIcon urgency={level.name} />
                  <Text size="sm" fw={700}>
                    {level.name}
                  </Text>
                </Group>
                <Text
                  size="xs"
                  c="dimmed"
                  className={classes.urgencyHeaderCount}
                >
                  {columnCounts[index]} samples
                </Text>
              </Box>
            );
          })}
        </Box>

        {shownCount < samples.length ? (
          <Text size="xs" c="dimmed" ta="right" mt={4}>
            Showing an even spread of {formatNumber(shownCount)} of{" "}
            {formatNumber(samples.length)} samples
          </Text>
        ) : null}

        <Box h={380}>
          <ResponsiveContainer width="100%" height="100%">
            <ScatterChart margin={SCATTER_MARGIN}>
              {URGENCY_LEVELS.map((level, index) => (
                <ReferenceArea
                  key={`area-${level.name}`}
                  x1={index}
                  x2={index + 1}
                  y1={0}
                  y2={yMax}
                  fill={mantineColor(URGENCY_COLORS[level.name])}
                  fillOpacity={index % 2 ? 0.04 : 0.08}
                  stroke="none"
                  ifOverflow="hidden"
                />
              ))}
              <CartesianGrid
                vertical={false}
                strokeDasharray="4 4"
                stroke="var(--mantine-color-default-border)"
              />
              {URGENCY_LEVELS.slice(1).map((level, index) => (
                <ReferenceLine
                  key={`divider-${level.name}`}
                  x={index + 1}
                  stroke="var(--mantine-color-default-border)"
                />
              ))}
              <ReferenceLine
                y={STALE_SCAN_DAYS}
                stroke={mantineColor(ERRANT_COLOR)}
                strokeDasharray="6 4"
                strokeOpacity={0.7}
                label={{
                  value: `Stale scan (${STALE_SCAN_DAYS}d)`,
                  position: "insideTopRight",
                  fill: mantineColor(ERRANT_COLOR),
                  fontSize: 11,
                  fontWeight: 600,
                }}
              />
              <XAxis
                type="number"
                dataKey="x"
                domain={[0, columnCount]}
                ticks={URGENCY_LEVELS.map((_, index) => index + 0.5)}
                tick={UrgencyRangeTick}
                interval={0}
                tickLine={false}
                axisLine={{ stroke: "var(--mantine-color-default-border)" }}
                height={44}
                label={{
                  value: "Time until OTB →",
                  position: "insideBottom",
                  fontSize: 12,
                  fill: "var(--mantine-color-dimmed)",
                }}
              />
              <YAxis
                type="number"
                dataKey="y"
                domain={[0, yMax]}
                ticks={yTicks}
                width={SCATTER_Y_AXIS_WIDTH}
                tickFormatter={(value) => `${formatNumber(value)}d`}
                tick={{ fontSize: 12, fill: "var(--mantine-color-dimmed)" }}
                tickLine={false}
                axisLine={false}
                label={{
                  value: "Days since scanned",
                  angle: -90,
                  position: "insideLeft",
                  offset: 10,
                  style: { textAnchor: "middle" },
                  fontSize: 12,
                  fill: "var(--mantine-color-dimmed)",
                }}
              />
              <Tooltip
                content={<UrgencyTooltip />}
                cursor={false}
                isAnimationActive={false}
              />
              {series.map((item) => (
                <Scatter
                  key={item.status}
                  name={item.status}
                  data={item.points}
                  shape={renderPoint}
                  className={
                    highlightStatus && highlightStatus !== item.status
                      ? classes.dimmedSeries
                      : classes.scatterSeries
                  }
                  isAnimationActive={false}
                />
              ))}
            </ScatterChart>
          </ResponsiveContainer>
        </Box>
      </Box>
    </Box>
  );
};

const SampleUrgencyPanel = ({
  isLoading,
  samples = [],
  otbLabel = "OTB Date",
  prefs,
  onPrefsChange,
}) => {
  // View and chips are saved (dashboardPrefs) so they survive a refresh. The
  // matrix is the default view until the user picks one.
  const view = prefs?.urgencyView ?? "matrix";
  const setView = (value) => onPrefsChange({ urgencyView: value });
  const statuses = prefs?.urgencyStatuses
    ? prefs.urgencyStatuses.filter((status) => SAMPLE_STATUSES.includes(status))
    : SAMPLE_STATUSES;
  const setStatuses = (value) => onPrefsChange({ urgencyStatuses: value });
  const [hoveredStatus, setHoveredStatus] = useState(null);

  const filtered = samples.filter((sample) => statuses.includes(sample.status));
  // Only highlight a hovered status that is actually shown, otherwise
  // hovering a deselected chip would grey out everything.
  const highlightStatus = statuses.includes(hoveredStatus)
    ? hoveredStatus
    : null;

  // Remember each view's height with every status shown and use it as the
  // min height, so deselecting statuses doesn't collapse the panel.
  const allSelected = statuses.length === SAMPLE_STATUSES.length;
  const { ref: contentRef, height: contentHeight } = useElementSize();
  const [fullHeights, setFullHeights] = useState({});

  useEffect(() => {
    if (allSelected && !isLoading && contentHeight > 0) {
      setFullHeights((heights) =>
        heights[view] === contentHeight
          ? heights
          : { ...heights, [view]: contentHeight },
      );
    }
  }, [allSelected, isLoading, contentHeight, view]);

  return (
    <Box mb={"md"}>
      <DashboardPanel
        title="Sample Urgency"
        count={filtered.length}
        actions={
          <SegmentedControl
            value={view}
            onChange={setView}
            data={[
              { value: "scatter", label: "Scatter" },
              { value: "matrix", label: "Matrix" },
            ]}
            style={{ minWidth: 220 }}
          />
        }
      >
        <SeriesToggle
          allLabel="All Statuses"
          options={STATUS_OPTIONS}
          value={statuses}
          onChange={setStatuses}
          onHover={setHoveredStatus}
        />

        <Box
          ref={contentRef}
          style={{
            minHeight: fullHeights[view],
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
          }}
        >
          {/* Cross-fade when switching Matrix <-> Scatter. */}
          <AnimatePresence mode="wait" initial={false}>
            <motion.div
              key={isLoading ? "loading" : view}
              initial={{ opacity: 0, y: 6 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -6 }}
              transition={{ duration: 0.2, ease: EASE_OUT }}
            >
              {isLoading ? (
                <Skeleton h={360} />
              ) : view === "scatter" ? (
                <UrgencyScatter
                  samples={filtered}
                  highlightStatus={highlightStatus}
                />
              ) : (
                <UrgencyMatrix
                  samples={filtered}
                  statuses={statuses}
                  highlightStatus={highlightStatus}
                  otbLabel={otbLabel}
                />
              )}
            </motion.div>
          </AnimatePresence>
        </Box>
      </DashboardPanel>
    </Box>
  );
};

const cellKey = (status, urgency) => `${status}|${urgency}`;

const MotionTr = motion.create(Table.Tr);

// Count heat: each urgency column is shaded in its priority colour
// (URGENCY_COLORS, as on the list's priority badges), so neighbouring counts
// read differently. The busiest cell is always the most prominent:
// - light mode: lightest to darkest across that hue's shades;
// - dark mode: the hue's main shade blended from faint (close to the page
//   background) to full strength, so quiet cells fade into the background.
const HEAT_SHADES = [0, 2, 4, 6, 8];
const DARK_HEAT_SHADE = 6;
const DARK_HEAT_MIN = 20;
const hueOf = (urgency) => (URGENCY_COLORS[urgency] ?? "gray.6").split(".")[0];
const heatStops = (urgency, dark) => {
  const hue = hueOf(urgency);
  if (dark) {
    const full = mantineColor(`${hue}.${DARK_HEAT_SHADE}`);
    return [
      `color-mix(in oklab, ${full} ${DARK_HEAT_MIN}%, var(--mantine-color-body))`,
      full,
    ];
  }
  return HEAT_SHADES.map((shade) => mantineColor(`${hue}.${shade}`));
};
// Count text colour on a cell: whichever of white or dark text contrasts more
// with the cell's actual colour (WCAG relative luminance). The colour is
// rebuilt from the theme's hex values, blended the same way as the CSS (in
// sRGB here, which is close enough to pick a text colour).
const hexToRgb = (hex) => {
  const value = parseInt(hex.replace("#", ""), 16);
  return [(value >> 16) & 255, (value >> 8) & 255, value & 255];
};
const mixRgb = (from, to, amount) =>
  from.map((channel, idx) => channel + (to[idx] - channel) * amount);
const luminance = (rgb) => {
  const [r, g, b] = rgb.map((channel) => {
    const c = channel / 255;
    return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
  });
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
};
const contrast = (a, b) => (Math.max(a, b) + 0.05) / (Math.min(a, b) + 0.05);
const DARK_TEXT = "dark.9";

const textColorFor = (theme, urgency, heat, dark) => {
  const shades = theme.colors[hueOf(urgency)] ?? theme.colors.gray;
  const stops = dark
    ? [
        mixRgb(
          hexToRgb(theme.colors.dark[7]),
          hexToRgb(shades[DARK_HEAT_SHADE]),
          DARK_HEAT_MIN / 100,
        ),
        hexToRgb(shades[DARK_HEAT_SHADE]),
      ]
    : HEAT_SHADES.map((shade) => hexToRgb(shades[shade]));
  const position = Math.min(Math.max(heat, 0), 1) * (stops.length - 1);
  const index = Math.min(Math.floor(position), stops.length - 2);
  const background = luminance(
    mixRgb(stops[index], stops[index + 1], position - index),
  );

  const white = contrast(background, 1);
  const darkText = contrast(
    background,
    luminance(hexToRgb(theme.colors.dark[9])),
  );
  return white >= darkText ? "white" : DARK_TEXT;
};

// 0 for the smallest non-zero cell up to 1 for the busiest. Square root so
// one very busy cell doesn't flatten the rest into the palest shade. One
// scale for the whole matrix, so a shade means the same count in any column.
const heatOf = (count, maxCount) =>
  maxCount > 1 ? Math.sqrt((count - 1) / (maxCount - 1)) : 1;

// Blends between the two ramp stops either side of the heat value.
const heatColor = (heat, stops) => {
  const position = Math.min(Math.max(heat, 0), 1) * (stops.length - 1);
  const index = Math.min(Math.floor(position), stops.length - 2);
  const mix = Math.round((position - index) * 100);
  return `color-mix(in oklab, ${stops[index + 1]} ${mix}%, ${stops[index]})`;
};

// Totals column: unshaded so it isn't read as part of the count heat scale.
const TOTAL_CELL_STYLE = { textAlign: "center" };

const UrgencyMatrix = ({ samples, statuses, highlightStatus, otbLabel }) => {
  const [openCell, setOpenCell] = useState(null);
  const dark = useComputedColorScheme("light") === "dark";
  const theme = useMantineTheme();

  const cellSamples = {};
  const rowTotals = {};
  const columnTotals = {};
  samples.forEach((sample) => {
    const key = cellKey(sample.status, sample.urgency);
    (cellSamples[key] = cellSamples[key] ?? []).push(sample);
    rowTotals[sample.status] = (rowTotals[sample.status] ?? 0) + 1;
    columnTotals[sample.urgency] = (columnTotals[sample.urgency] ?? 0) + 1;
  });
  const maxCount = Math.max(
    1,
    ...Object.values(cellSamples).map((items) => items.length),
  );
  const rows = SAMPLE_STATUSES.filter((status) => statuses.includes(status));

  if (!rows.length) {
    return (
      <Text c="dimmed" ta="center" py={"xl"}>
        Select a status to view the matrix
      </Text>
    );
  }

  return (
    <Table.ScrollContainer minWidth={URGENCY_MIN_WIDTH}>
      <Table withTableBorder withColumnBorders style={{ tableLayout: "fixed" }}>
        <Table.Thead>
          <Table.Tr>
            <Table.Th style={{ width: 160 }} />
            {URGENCY_LEVELS.map((level) => (
              <Table.Th key={level.name} style={{ textAlign: "center" }}>
                <Group gap={6} justify="center" wrap="nowrap">
                  <UrgencyIcon urgency={level.name} />
                  <Text size="sm" fw={700}>
                    {level.name}
                  </Text>
                </Group>
                <Text size="xs" c="dimmed">
                  {level.range}
                </Text>
              </Table.Th>
            ))}
            <Table.Th style={{ ...TOTAL_CELL_STYLE, width: 96 }}>
              <Text size="sm" fw={700}>
                Total
              </Text>
            </Table.Th>
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {rows.map((status) => (
            <MotionTr
              key={status}
              className={
                highlightStatus && highlightStatus !== status
                  ? classes.dimmed
                  : undefined
              }
              // Motion owns opacity (fade in when a status is switched back
              // on, and the hover dim); the class still adds the greyscale.
              initial={{ opacity: 0 }}
              animate={{
                opacity:
                  highlightStatus && highlightStatus !== status ? 0.25 : 1,
              }}
              transition={{ duration: 0.25 }}
              style={{ transition: "filter 200ms" }}
            >
              <Table.Td>
                {/* Same status dot as the stat tiles / scatter points. */}
                <Indicator
                  zIndex={1}
                  position="middle-start"
                  color={STATUS_COLORS[status]}
                  disabled={!STATUS_COLORS[status]}
                >
                  <Text size="sm" style={{ paddingLeft: 12 }}>
                    {status}
                  </Text>
                </Indicator>
              </Table.Td>
              {URGENCY_LEVELS.map((level) => {
                const key = cellKey(status, level.name);
                const items = cellSamples[key] ?? [];
                const count = items.length;
                const heat = count ? heatOf(count, maxCount) : 0;
                const isOpen = openCell === key;
                return (
                  <Table.Td
                    key={level.name}
                    style={{
                      padding: 0,
                      textAlign: "center",
                      backgroundColor: count
                        ? heatColor(heat, heatStops(level.name, dark))
                        : undefined,
                    }}
                  >
                    {count ? (
                      <HoverCard
                        position="top"
                        withArrow
                        shadow="md"
                        width={280}
                        openDelay={120}
                        closeDelay={80}
                        onOpen={() => setOpenCell(key)}
                        onClose={() =>
                          setOpenCell((current) =>
                            current === key ? null : current,
                          )
                        }
                      >
                        <HoverCard.Target>
                          <UnstyledButton
                            className={`${classes.matrixCell} ${
                              isOpen ? classes.matrixCellActive : ""
                            }`}
                            aria-label={`${formatNumber(count)} ${status} samples, ${level.name}`}
                          >
                            <Text
                              size="sm"
                              fw={600}
                              c={textColorFor(theme, level.name, heat, dark)}
                            >
                              {formatNumber(count)}
                            </Text>
                          </UnstyledButton>
                        </HoverCard.Target>
                        <HoverCard.Dropdown>
                          <MatrixCellDetail
                            status={status}
                            level={level}
                            items={items}
                            rowTotal={rowTotals[status] ?? 0}
                            columnTotal={columnTotals[level.name] ?? 0}
                            otbLabel={otbLabel}
                          />
                        </HoverCard.Dropdown>
                      </HoverCard>
                    ) : (
                      <Text
                        size="sm"
                        c="dimmed"
                        className={classes.matrixCellEmpty}
                      >
                        0
                      </Text>
                    )}
                  </Table.Td>
                );
              })}
              <Table.Td style={{ ...TOTAL_CELL_STYLE, padding: 0 }}>
                <Text
                  size="sm"
                  fw={700}
                  c={rowTotals[status] ? undefined : "dimmed"}
                  className={classes.matrixCellEmpty}
                >
                  {formatNumber(rowTotals[status] ?? 0)}
                </Text>
              </Table.Td>
            </MotionTr>
          ))}
        </Table.Tbody>
      </Table>
      {/* What the columns mean: priority is the time left until OTB. */}
      {/* <Group gap={6} mt="xs" wrap="nowrap">
        <IconInfoCircle size={14} color="var(--mantine-color-dimmed)" />
        <Text size="xs" c="dimmed">
          Columns show each sample's priority relative to its {otbLabel}: P0 is
          due today or overdue, P1-P8 are due within that many weeks, Low is 8+
          weeks away and Error has no {otbLabel}. Stronger colour means more
          samples.
        </Text>
      </Group> */}
    </Table.ScrollContainer>
  );
};

const percentOf = (value, total) =>
  total ? `${Math.round((value / total) * 100)}%` : "-";

const MetaRow = ({ label, value, valueColor }) => (
  <Group justify="space-between" gap={"md"} wrap="nowrap">
    <Text size="xs" c="dimmed">
      {label}
    </Text>
    <Text size="xs" fw={600} c={valueColor}>
      {value}
    </Text>
  </Group>
);

// Popover content for a matrix cell: where the cell sits within its status
// row and urgency column, scan freshness, and the samples most in need of a
// rescan.
// How an urgency level sits against the OTB date, e.g. "1-2 Weeks until OTB
// Date". Only P0 can be on or past it; Error has no date.
const urgencyTiming = (level, otbLabel = "OTB Date") => {
  if (level.name === "P0") return `${otbLabel} today or past`;
  if (level.name === "Error") return `No ${otbLabel}`;
  return `${level.range} until ${otbLabel}`;
};

const MatrixCellDetail = ({
  status,
  level,
  items,
  rowTotal,
  columnTotal,
  otbLabel,
}) => {
  const days = items.map((sample) => sample.daysSinceScanned);
  const avgDays = Math.round(
    days.reduce((sum, value) => sum + value, 0) / days.length,
  );
  const staleCount = days.filter((value) => value > STALE_SCAN_DAYS).length;
  const oldest = [...items]
    .sort((a, b) => b.daysSinceScanned - a.daysSinceScanned)
    .slice(0, 3);

  return (
    <Stack gap={"sm"}>
      <Group gap={"xs"} wrap="nowrap">
        <Box
          w={10}
          h={10}
          style={{
            borderRadius: "50%",
            flexShrink: 0,
            backgroundColor: mantineColor(STATUS_COLORS[status]),
          }}
        />
        <Text size="sm" fw={700}>
          {status}
        </Text>
        <Text size="sm" c="dimmed">
          ·
        </Text>
        <UrgencyIcon urgency={level.name} size={20} iconSize={12} />
        <Text size="sm" fw={700}>
          {level.name}
        </Text>
      </Group>

      <Group gap={6} align="baseline">
        <Text fw={700} size={"1.5rem"} lh={1}>
          {formatNumber(items.length)}
        </Text>
        <Text size="sm" c="dimmed">
          sample{items.length === 1 ? "" : "s"} ·{" "}
          {urgencyTiming(level, otbLabel)}
        </Text>
      </Group>

      <Stack gap={4}>
        <MetaRow
          label={`Share of ${status}`}
          value={percentOf(items.length, rowTotal)}
        />
        <MetaRow
          label={`Share of ${level.name}`}
          value={percentOf(items.length, columnTotal)}
        />
        <MetaRow
          label="Avg. since last scan"
          value={`${formatNumber(avgDays)} days`}
        />
        <MetaRow
          label={`Stale (>${STALE_SCAN_DAYS} days)`}
          value={formatNumber(staleCount)}
          valueColor={
            staleCount ? `${ERRANT_COLOR.split(".")[0]}.6` : undefined
          }
        />
      </Stack>

      {/* <Divider /> */}

      {/* <Stack gap={4}>
        <Text
          size="xs"
          c="dimmed"
          fw={700}
          tt="uppercase"
          className={classes.label}
        >
          Longest since scan
        </Text>
        {oldest.map((sample) => (
          <MetaRow
            key={sample.id}
            label={sample.id}
            value={`${formatNumber(sample.daysSinceScanned)} days`}
            valueColor={
              sample.daysSinceScanned > STALE_SCAN_DAYS
                ? `${ERRANT_COLOR.split(".")[0]}.6`
                : undefined
            }
          />
        ))}
      </Stack> */}
    </Stack>
  );
};

// Rows with `children` become buttons that call onItemClick (drill down) and
// show a chevron; other rows keep a matching gap so the values line up.
const VolumeList = ({
  isLoading,
  items = [],
  barColor = "brand.6",
  onItemClick,
}) => {
  if (isLoading) {
    return (
      <Stack gap={"md"}>
        {[1, 2, 3, 4, 5].map((val) => (
          <Skeleton key={val} h={16} />
        ))}
      </Stack>
    );
  }

  const maxValue = Math.max(1, ...items.map((item) => item.value));
  const canDrill = (item) => Boolean(onItemClick && item.children?.length);
  const anyDrill = items.some(canDrill);

  return (
    <Stack gap={"md"}>
      {items.map((item, index) => {
        const drill = canDrill(item);
        const row = (
          <Group justify="space-between" wrap="nowrap" gap={"md"}>
            <Text
              size="sm"
              style={{ flex: "0 0 35%", minWidth: 0 }}
              truncate="end"
            >
              {item.label}
            </Text>
            <Box style={{ flexGrow: 1, position: "relative" }}>
              <Box
                h={10}
                style={{
                  borderRadius: 4,
                  backgroundColor: "var(--mantine-color-default-border)",
                }}
              />
              <motion.div
                initial={{ width: 0 }}
                animate={{ width: `${(item.value / maxValue) * 100}%` }}
                // Starts once the panel has faded in, then staggers per row.
                transition={{
                  duration: 0.7,
                  delay: 0.3 + index * 0.05,
                  ease: EASE_OUT,
                }}
                style={{
                  height: 10,
                  borderRadius: 4,
                  position: "absolute",
                  top: 0,
                  left: 0,
                  backgroundColor: mantineColor(item.color ?? barColor),
                }}
              />
            </Box>
            <Text
              size="sm"
              fw={600}
              style={{ flexShrink: 0, width: 48, textAlign: "right" }}
            >
              {formatNumber(item.value)}
            </Text>
            {anyDrill ? (
              <Box w={14} style={{ flexShrink: 0, display: "flex" }}>
                {drill ? (
                  <IconChevronRight
                    size={14}
                    color="var(--mantine-color-dimmed)"
                  />
                ) : null}
              </Box>
            ) : null}
          </Group>
        );
        return drill ? (
          <UnstyledButton
            key={item.label}
            onClick={() => onItemClick(item)}
            aria-label={`${item.label}: ${formatNumber(item.value)}, show subcategories`}
          >
            {row}
          </UnstyledButton>
        ) : (
          <Box key={item.label}>{row}</Box>
        );
      })}
    </Stack>
  );
};

// Product categories; clicking one drills into its subcategories. The open
// category is kept by label, and closes if a date/filter change removes it.
const CategoriesPanel = ({ isLoading, categories = [] }) => {
  const navigate = useNavigate();
  const [openLabel, setOpenLabel] = useState(null);
  const open = categories.find(
    (item) => item.label === openLabel && item.children,
  );

  return (
    <DashboardPanel
      title={open ? `Categories  ›  ${open.label}` : "Categories"}
      count={sumValues(open ? open.children : categories)}
      onViewAll={() => navigate(INVENTORY_LIST_ROUTE)}
    >
      {open ? (
        // Back link tucked up under the title (the header's margin is lg).
        <UnstyledButton
          onClick={() => setOpenLabel(null)}
          mt={"calc(var(--mantine-spacing-lg) * -0.75)"}
          mb={"md"}
        >
          <Group gap={4} wrap="nowrap">
            <IconChevronLeft size={14} color="var(--mantine-color-dimmed)" />
            <Text size="sm" c="dimmed">
              All categories
            </Text>
          </Group>
        </UnstyledButton>
      ) : null}
      <VolumeList
        // Remount so the bars animate in again for the new level.
        key={open ? open.label : "all"}
        isLoading={isLoading}
        items={open ? open.children : categories}
        barColor={CATEGORY_COLOR}
        onItemClick={open ? undefined : (item) => setOpenLabel(item.label)}
      />
    </DashboardPanel>
  );
};

// Horizontal bar per stage, coloured to match the Sample Urgency status chips.
// It fills whatever height its panel has, so on wide screens it lines up with
// the Inventory Overview beside it; stacked, it falls back to a min height.
const StageBarChart = ({ isLoading, items = [] }) => {
  const data = items.map((item) => ({
    stage: item.label,
    Samples: item.value,
    color: STATUS_COLORS[item.label] ?? "brand.6",
  }));

  return (
    <Box style={{ flex: 1, position: "relative", minHeight: 280 }}>
      <Box style={{ position: "absolute", inset: 0 }}>
        {isLoading ? (
          <Skeleton h={"100%"} />
        ) : (
          <BarChart
            className={classes.chartAnimateIn}
            h={"100%"}
            data={data}
            dataKey="stage"
            orientation="vertical"
            series={[{ name: "Samples", color: "brand.6" }]}
            gridAxis="x"
            withXAxis={false}
            withBarValueLabel
            maxBarWidth={28}
            yAxisProps={{ width: 120 }}
            // Headroom so the longest bar's value label isn't clipped.
            xAxisProps={{ domain: [0, (max) => Math.ceil(max * 1.15)] }}
            valueFormatter={formatNumber}
            barProps={{ radius: [0, 4, 4, 0] }}
          />
        )}
      </Box>
    </Box>
  );
};

// Each channel gets its own shade of the category blue.
const ChannelsPanel = ({ isLoading, channels = [] }) => {
  const navigate = useNavigate();

  return (
    <DashboardPanel
      title="Channels"
      count={sumValues(channels)}
      onViewAll={() => navigate(INVENTORY_LIST_ROUTE)}
    >
      <VolumeList
        isLoading={isLoading}
        items={channels}
        barColor={CHANNEL_COLOR}
      />
    </DashboardPanel>
  );
};

// Date formats used by distribution records, matching DistributionList.
const DISTRIBUTION_DATE_FORMATS = [
  "YYYY-MM-DD-HH-mm-ss-SSS",
  "YYYY-MM-DD-HH-mm-ss",
  "YYYY-MM-DD",
];

const parseDistributionDate = (value) =>
  value ? moment(value, DISTRIBUTION_DATE_FORMATS) : null;

// Timestamp for sorting; missing or unparseable dates count as 0 (sort last).
const distributionTime = (value) => {
  const date = parseDistributionDate(value);
  return date?.isValid() ? date.valueOf() : 0;
};

// Newest distribution first; styles without a valid date go last.
const sortByDistributionDate = (styles = []) =>
  [...(styles ?? [])].sort(
    (a, b) =>
      distributionTime(b.distributionDate) -
      distributionTime(a.distributionDate),
  );

const HERO_HEIGHT = 180;
const STRIP_THUMB_SIZE = 36;
const STRIP_MAX_THUMBS = 3;

// Horizontal slider of the latest distributed styles. Native scrolling (so
// touch/trackpad swipe works) with scroll-snap, arrow buttons that page with
// an eased scroll, and edges that fade out where more cards are hidden.
const LatestDistributionsSlider = ({
  isLoading,
  items = [],
  user,
  onAssetClick,
}) => {
  const trackRef = useRef(null);
  const scrollAnimation = useRef(null);
  const reduceMotion = useReducedMotion();
  const [resizeRef, trackSize] = useResizeObserver();
  const [edges, setEdges] = useState({ start: true, end: true });

  const updateEdges = () => {
    const track = trackRef.current;
    if (!track) return;
    const maxScroll = track.scrollWidth - track.clientWidth;
    const next = {
      start: track.scrollLeft <= 1,
      end: track.scrollLeft >= maxScroll - 1,
    };
    setEdges((prev) =>
      prev.start === next.start && prev.end === next.end ? prev : next,
    );
  };

  // Re-check when the track resizes or its cards change.
  useEffect(updateEdges, [trackSize.width, items.length, isLoading]);

  useEffect(() => () => scrollAnimation.current?.stop(), []);

  // Page to the next/previous set of cards, landing on a card's start edge.
  const page = (direction) => {
    const track = trackRef.current;
    if (!track) return;

    const maxScroll = track.scrollWidth - track.clientWidth;
    const offsets = Array.from(track.children).map((child) => child.offsetLeft);
    const { scrollLeft, clientWidth } = track;

    let target =
      direction > 0
        ? // First card that isn't fully visible on the right.
          (offsets.find((offset, index) => {
            const child = track.children[index];
            return offset + child.offsetWidth > scrollLeft + clientWidth + 1;
          }) ?? maxScroll)
        : // Earliest card that still fits when it becomes the last visible.
          (offsets.find((offset) => offset >= scrollLeft - clientWidth - 1) ??
          0);
    target = Math.max(0, Math.min(maxScroll, target));

    scrollAnimation.current?.stop();
    if (reduceMotion) {
      track.scrollLeft = target;
      return;
    }

    // Snap fights a frame-by-frame scroll, so pause it while animating.
    track.style.scrollSnapType = "none";
    scrollAnimation.current = animate(scrollLeft, target, {
      duration: 0.55,
      ease: EASE_OUT,
      onUpdate: (latest) => {
        track.scrollLeft = latest;
      },
      onComplete: () => {
        track.style.scrollSnapType = "";
      },
    });
  };

  if (!isLoading && !items.length) {
    return <EmptyState showImage={false} style={{ padding: 20 }} />;
  }

  const arrow = (direction) => {
    const hidden = direction < 0 ? edges.start : edges.end;
    const Icon = direction < 0 ? IconChevronLeft : IconChevronRight;
    return (
      <AnimatePresence>
        {!hidden ? (
          <motion.div
            key={direction}
            className={`${classes.sliderArrow} ${
              direction < 0 ? classes.sliderArrowLeft : classes.sliderArrowRight
            }`}
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.8 }}
            transition={{ duration: 0.2 }}
          >
            <ActionIcon
              variant="default"
              radius={"xl"}
              size={"xl"}
              aria-label={
                direction < 0 ? "Previous distributions" : "Next distributions"
              }
              onClick={() => page(direction)}
              style={{ boxShadow: "var(--mantine-shadow-md)" }}
            >
              <Icon size={20} stroke={1.75} />
            </ActionIcon>
          </motion.div>
        ) : null}
      </AnimatePresence>
    );
  };

  return (
    <Box className={classes.slider}>
      <div
        ref={(node) => {
          trackRef.current = node;
          resizeRef.current = node;
        }}
        className={classes.sliderTrack}
        style={{
          "--fade-start": edges.start ? "0px" : "64px",
          "--fade-end": edges.end ? "0px" : "64px",
        }}
        onScroll={updateEdges}
        tabIndex={0}
        role="region"
        aria-label="Latest distributions"
        onKeyDown={(event) => {
          if (event.key === "ArrowRight") {
            event.preventDefault();
            page(1);
          } else if (event.key === "ArrowLeft") {
            event.preventDefault();
            page(-1);
          }
        }}
      >
        {isLoading
          ? Array.from({ length: 5 }, (_, index) => (
              <div key={index} className={classes.sliderSlide}>
                <Paper withBorder radius={"md"} style={{ overflow: "hidden" }}>
                  <Skeleton h={HERO_HEIGHT} radius={0} />
                  <Stack gap={8} p={"sm"}>
                    <Skeleton h={12} w={"60%"} />
                    <Skeleton h={10} w={"80%"} />
                    <Skeleton h={10} w={"40%"} />
                  </Stack>
                </Paper>
              </div>
            ))
          : items.map((item, index) => (
              <motion.div
                key={item.styleNumber}
                className={classes.sliderSlide}
                initial={{ opacity: 0, x: 24 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{
                  duration: 0.4,
                  delay: Math.min(index, 6) * 0.05,
                  ease: EASE_OUT,
                }}
              >
                <DistributionCard
                  item={item}
                  user={user}
                  onAssetClick={onAssetClick}
                />
              </motion.div>
            ))}
      </div>

      {arrow(-1)}
      {arrow(1)}
    </Box>
  );
};

// Days the style was distributed ahead of (+) or behind (-) its OTB date.
const OnTimeBadge = ({ otbDate, distributionDate, otbLabel }) => {
  if (!otbDate || !distributionDate) return null;
  const otb = moment(otbDate, DISTRIBUTION_DATE_FORMATS);
  if (!otb.isValid() || !distributionDate.isValid()) return null;

  const days = otb
    .clone()
    .startOf("day")
    .diff(distributionDate.clone().startOf("day"), "days");
  const late = days < 0;
  const label =
    days === 0 ? "On OTB" : late ? `${-days}d late` : `${days}d early`;

  return (
    <Badge
      size="sm"
      variant="light"
      color={late ? TREND_BAD : TREND_GOOD}
      title={`${otbLabel}: ${otb.format("DD/MM/YYYY")}`}
    >
      {label}
    </Badge>
  );
};

const DistributionCard = ({ item, user, onAssetClick }) => {
  const assets = item.assets;
  const hero = assets?.[0];
  const distributed = parseDistributionDate(item.distributionDate);
  const otbLabel = user?.clientCode
    ? getClientLabel(user.clientCode, "otbDate")
    : "OTB Date";
  const assetName = `${item.styleNumber} - ${item.name}`;

  return (
    <Paper withBorder radius={"md"} className={classes.distributionCard}>
      <Box
        h={HERO_HEIGHT}
        style={{
          overflow: "hidden",
          backgroundColor: "var(--mantine-color-default-hover)",
        }}
      >
        {!assets ? (
          <Skeleton h={"100%"} radius={0} />
        ) : hero ? (
          <ImagePreview
            file={{ url: hero.preview ?? hero.thumb }}
            onClick={() => onAssetClick({ ...hero, name: assetName })}
            height={HERO_HEIGHT}
            width={"100%"}
            fit="cover"
          />
        ) : (
          <Stack h={"100%"} align="center" justify="center" gap={4}>
            <IconPhoto
              size={24}
              stroke={1.5}
              color="var(--mantine-color-dimmed)"
            />
            <Text size="xs" c="dimmed">
              No assets
            </Text>
          </Stack>
        )}
      </Box>

      {assets?.length > 1 ? (
        <Box px={"sm"} pt={"sm"}>
          <AssetThumbs
            styleItem={item}
            onAssetClick={onAssetClick}
            size={STRIP_THUMB_SIZE}
            max={STRIP_MAX_THUMBS}
            skip={1}
          />
        </Box>
      ) : null}

      <Stack gap={4} p={"sm"}>
        <Link className="router-link" to={`/distributions/${item.styleNumber}`}>
          <Text size="sm" fw={700} truncate>
            {item.styleNumber}
          </Text>
        </Link>
        <Text size="sm" c="dimmed" truncate title={item.name}>
          {item.name}
        </Text>
        {item.dept ? (
          <Group gap={4}>
            <Badge size="sm" variant="default" radius={"sm"}>
              {item.dept}
            </Badge>
          </Group>
        ) : null}
        <Group justify="space-between" gap={"xs"} wrap="nowrap" mt={4}>
          <Text
            size="xs"
            c="dimmed"
            title={
              distributed?.isValid()
                ? distributed.format("DD/MM/YYYY HH:mm")
                : undefined
            }
          >
            {distributed?.isValid() ? distributed.fromNow() : "Not distributed"}
          </Text>
          <OnTimeBadge
            otbDate={item.otbDate}
            distributionDate={distributed}
            otbLabel={otbLabel}
          />
        </Group>
      </Stack>
    </Paper>
  );
};

const formatDistributionDate = (value) => {
  if (!value) return "";
  const date = moment(value, DISTRIBUTION_DATE_FORMATS);
  return date.isValid() ? date.format("DD/MM/YYYY") : value;
};

const columnHelper = createColumnHelper();

// Styled to match the HomeModule / DistributionList tables: bordered wrapper,
// the global `h-scroll-table` classes from App.module.scss and the shared
// sortable `Th` header.
// Not currently rendered (replaced by LatestDistributionsSlider); exported so it
// stays available to switch back to.
export const LatestDistributionsTable = ({
  isLoading,
  items = [],
  user,
  onAssetClick,
}) => {
  const [sorting, setSorting] = useState([]);

  const columns = useMemo(
    () => [
      columnHelper.accessor("styleNumber", {
        header: "Style Number",
        size: 180,
        cell: (info) => (
          <Link
            className="router-link"
            to={`/distributions/${info.getValue()}`}
          >
            {info.getValue()}
          </Link>
        ),
      }),
      columnHelper.accessor("name", {
        header: "Name",
        size: 300,
        cell: (info) => (
          <Text size="sm" truncate>
            {info.getValue()}
          </Text>
        ),
      }),
      columnHelper.display({
        id: "assets",
        header: "Assets",
        size: 300,
        enableSorting: false,
        cell: (info) => (
          <AssetThumbs
            styleItem={info.row.original}
            onAssetClick={onAssetClick}
          />
        ),
      }),
      columnHelper.accessor("dept", {
        header: "Department",
        size: 180,
        cell: (info) => (
          <Text size="sm" truncate>
            {info.getValue()}
          </Text>
        ),
      }),
      columnHelper.accessor("otbDate", {
        header: user?.clientCode
          ? getClientLabel(user.clientCode, "otbDate")
          : "OTB Date",
        size: 180,
        cell: (info) => (
          <Text size="sm">{formatDistributionDate(info.getValue())}</Text>
        ),
      }),
      columnHelper.accessor("distributionDate", {
        header: "Distribution Date",
        size: 200,
        cell: (info) => (
          <Text size="sm">{formatDistributionDate(info.getValue())}</Text>
        ),
      }),
    ],
    [user?.clientCode, onAssetClick],
  );

  const table = useReactTable({
    data: items,
    columns,
    state: { sorting },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  if (!isLoading && !items.length) {
    return <EmptyState showImage={false} style={{ padding: 20 }} />;
  }

  return (
    <div className="table-wrapper">
      <div
        className="h-scroll-table-container"
        style={{ height: "100%", overflowX: "auto" }}
      >
        <Table
          withRowBorders
          withColumnBorders
          highlightOnHover
          size="sm"
          className="h-scroll-table"
          style={{ marginBottom: 0 }}
        >
          <Table.Thead>
            {table.getHeaderGroups().map((headerGroup) => (
              <Table.Tr key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <Th
                    key={header.id}
                    column={header.column}
                    onClick={header.column.getToggleSortingHandler()}
                    reversed={header.column.getIsSorted() !== "asc"}
                    sorted={header.column.getIsSorted() ? true : undefined}
                    className={["scrollTableHeader", "scrolled"]}
                    style={{
                      cursor: "pointer",
                      minWidth: `${header.getSize()}px`,
                    }}
                  >
                    {header.isPlaceholder ? null : header.column.getCanSort() ? (
                      flexRender(
                        header.column.columnDef.header,
                        header.getContext(),
                      )
                    ) : (
                      // Th zeroes cell padding and relies on its sort button
                      // for spacing, so mirror that for non-sortable headers.
                      <div
                        style={{
                          padding:
                            "var(--mantine-spacing-xs) var(--mantine-spacing-md)",
                          fontSize: "var(--mantine-font-size-sm)",
                          fontWeight: 500,
                        }}
                      >
                        {flexRender(
                          header.column.columnDef.header,
                          header.getContext(),
                        )}
                      </div>
                    )}
                  </Th>
                ))}
              </Table.Tr>
            ))}
          </Table.Thead>
          <Table.Tbody>
            {isLoading
              ? Array.from({ length: LATEST_DISTRIBUTIONS_COUNT }, (_, row) => (
                  <Table.Tr key={row}>
                    {columns.map((column, idx) => (
                      <Table.Td key={idx}>
                        <Skeleton height={8} my="sm" />
                      </Table.Td>
                    ))}
                  </Table.Tr>
                ))
              : table.getRowModel().rows.map((row) => (
                  <Table.Tr key={row.id}>
                    {row.getVisibleCells().map((cell) => (
                      <Table.Td
                        key={cell.id}
                        style={{ verticalAlign: "middle" }}
                      >
                        {flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext(),
                        )}
                      </Table.Td>
                    ))}
                  </Table.Tr>
                ))}
          </Table.Tbody>
        </Table>
      </div>
    </div>
  );
};

const THUMB_SIZE = 48;
const MAX_THUMBS = 5;

// Asset thumbnails for a style. Assets are filled in by fetchLatestStyles, so
// until that lands the row shows placeholder tiles. `skip` leaves out leading
// assets (e.g. one already shown as a card's hero image).
const AssetThumbs = ({
  styleItem,
  onAssetClick,
  size = THUMB_SIZE,
  max = MAX_THUMBS,
  skip = 0,
}) => {
  const assets = styleItem.assets?.slice(skip);

  if (!assets) {
    return (
      <Group gap={"xs"} wrap="nowrap">
        {Array.from({ length: max }, (_, index) => (
          <Skeleton key={index} w={size} h={size} radius={4} />
        ))}
      </Group>
    );
  }

  if (!assets.length) {
    return (
      <Group gap={6} wrap="nowrap">
        <IconPhoto size={16} stroke={1.5} color="var(--mantine-color-dimmed)" />
        <Text size="xs" c="dimmed">
          No assets
        </Text>
      </Group>
    );
  }

  const remaining = assets.length - max;

  return (
    <Group gap={"xs"} wrap="nowrap">
      {assets.slice(0, max).map((asset, index) => (
        <Box
          key={asset.id ?? index}
          w={size}
          h={size}
          style={{
            borderRadius: 4,
            overflow: "hidden",
            flexShrink: 0,
            backgroundColor: "var(--mantine-color-default-hover)",
          }}
        >
          <ImagePreview
            file={{ url: asset.thumb }}
            onClick={() =>
              onAssetClick({
                ...asset,
                name: `${styleItem.styleNumber} - ${styleItem.name}`,
              })
            }
            height={size}
            width={size}
            fit="cover"
          />
        </Box>
      ))}
      {remaining > 0 ? (
        <Box
          component={Link}
          to={`/distributions/${styleItem.styleNumber}`}
          title={`${remaining} more asset${remaining === 1 ? "" : "s"}`}
          w={size}
          h={size}
          style={{
            borderRadius: 4,
            flexShrink: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            textDecoration: "none",
            backgroundColor: "var(--mantine-color-default-hover)",
            border: "1px solid var(--mantine-color-default-border)",
          }}
        >
          <Text size="sm" c="dimmed" fw={600}>
            +{remaining}
          </Text>
        </Box>
      ) : null}
    </Group>
  );
};
